import { Fragment } from 'react';
import { LockIcon } from 'lucide-react';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { PROVIDER_KEY_TO_NAME } from 'src/shared/constants';
import { Badge } from '@/components/ui/badge';
import { logoMap, renderLogo } from 'src/utils/renderLogo';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { cn } from 'src/lib/utils';

import type { IIntegrationsQuery } from 'src/graphql/actions/integration/integrations.query';

const PermissionTooltip = ({ children }: React.PropsWithChildren) => {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{children}</div>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" className="max-w-50">
          You need permission to use this integration. Contact the integration owner.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const IntegrationItemSkeleton = () => {
  return (
    <Card>
      <CardHeader className="pb-4">
        <Skeleton className="h-5 w-16 rounded-full" />
      </CardHeader>
      <CardContent className="flex items-center gap-4">
        <Skeleton className="size-10 rounded shrink-0" />
        <div className="space-y-1.5 grow min-w-0">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-4 w-32" />
        </div>
      </CardContent>
      <CardFooter className="gap-1 py-3 border-t">
        <Skeleton className="size-6 rounded-full shrink-0" />
        <Skeleton className="h-4 w-20" />
      </CardFooter>
    </Card>
  );
};

const Owner = ({ data }: { data: Props['integration']['owner'] }) => {
  if (data)
    return (
      <Fragment>
        <Avatar className="size-6 md:size-6 rounded-full shrink-0">
          <AvatarImage src={getGravatarUrl(data?.email ?? '')} />
          <AvatarFallback className="rounded-full bg-primary text-white">
            {data?.name?.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="text-xs text-gray-500 truncate grow min-w-0">{data?.name}</div>
      </Fragment>
    );

  return (
    <Fragment>
      <span className="size-6 rounded-full shrink-0 bg-muted border border-border" />
      <Badge variant="outline" className="font-medium">
        Deleted user
      </Badge>
    </Fragment>
  );
};

type Props = {
  integration: IIntegrationsQuery['integrations']['nodes'][number];
  onClick?: (integrationId: string) => void;
};

const IntegrationCard = ({ integration, onClick }: Props) => {
  const { id, provider, accountName, tenantId, owner, policy } = integration;
  const providerName = PROVIDER_KEY_TO_NAME[provider as keyof typeof PROVIDER_KEY_TO_NAME];
  const Icon = renderLogo(provider as keyof typeof logoMap);

  const handleClick = () => onClick?.(id);

  return (
    <Card
      onClick={handleClick}
      className={cn(
        'cursor-pointer hover:bg-accent transition-colors duration-200',
        !policy.read && 'bg-muted border-muted hover:bg-muted cursor-not-allowed',
      )}
      role="button"
      tabIndex={0}
    >
      <CardHeader className="flex-row justify-between items-center space-y-0 gap-2 pb-4">
        <Badge className="bg-green-100 text-green-800">Active</Badge>
        {!policy.read && <LockIcon className="size-3 text-foreground" />}
      </CardHeader>
      <CardContent className="flex items-center gap-4">
        {Icon && <Icon className="size-10 shrink-0" />}
        <div className="space-y-1.5 grow min-w-0">
          <CardTitle className="font-semibold truncate tracking-tight">{providerName}</CardTitle>
          <CardDescription className="text-xs text-foreground font-semibold truncate">
            {accountName} {tenantId && `(${tenantId})`}
          </CardDescription>
        </div>
      </CardContent>
      <CardFooter className="gap-1 py-3 border-t">
        <Owner data={owner} />
      </CardFooter>
    </Card>
  );
};

const IntegrationItem = ({ integration, onClick }: Props) => {
  if (integration.policy.read) return <IntegrationCard integration={integration} onClick={onClick} />;

  return (
    <PermissionTooltip>
      <IntegrationCard integration={integration} />
    </PermissionTooltip>
  );
};

export { IntegrationItem, IntegrationItemSkeleton };
